import React from 'react';
import PropTypes from 'prop-types';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import Main from 'App/components/styles/Main/Main';
import { Helmet } from 'react-helmet';
import { catchPhrase } from 'App/components/config/config';
import PageTitle from 'App/components/styles/PageTitle/PageTitle';
import PageWrapper from 'App/components/styles/PageWrapper/PageWrapper';
import { NotFound } from 'App/components/errors';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import * as R from 'ramda';
import PageSpinner from 'App/components/PageSpinner';
import styled from '@emotion/styled';
import { cx } from '@emotion/css';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { Button } from 'react-bootstrap';
import useToggleStateOnUser from 'App/hooks/useToggleStateOnUser';

const propTypes = {
    permissionType: PropTypes.oneOf(R.keys(PERMISSION_TYPES)).isRequired,
    children: PropTypes.node,
    title: PropTypes.string.isRequired,
    showPageTitle: PropTypes.bool,
    showNotFound: PropTypes.bool,
    sidebar: PropTypes.node.isRequired,
    isLoading: PropTypes.bool,
};

const defaultProps = {
    showPageTitle: true,
    showNotFound: false,
    isLoading: false,
    children: null,
};

const PageWrapperWithSidebar = styled('div')`
    display: flex;
    width: 100%;

    aside {
        background-color: var(--sideBarBackground);
        color: var(--sideBarColor);
        overflow: auto;
        display: flex;
        height: calc(100vh - var(--headerHeight) - var(--navbarHeight) - var(--footerHeight));
        min-width: 40px;

        a {
            cursor: pointer;
            font-size: 12px;
        }

        .menu {
            width: 100%;
        }

        .menu-collapse {
            flex-basis: 40px;
            text-align: center;
            padding-top: 10px;
        }
    }

    article {
        flex-grow: 1;
    }

    &.menu-closed {
        aside {
            .menu {
                display: none;
            }
        }
    }
`;

function PageWithSidebar({ permissionType, children, title, showPageTitle, isLoading, showNotFound, sidebar }) {
    const hasPermission = useIsPermitted(permissionType);

    const stateKey = `${title}-page-sidebar`;

    const [isSideBarOpen, toggleSidebar] = useToggleStateOnUser(stateKey);

    if (isLoading) {
        return <PageSpinner />;
    }

    return (
        <Main>
            <Helmet>
                <title>
                    {hasPermission && !showNotFound ? title : 'Not Found'} - {catchPhrase}
                </title>
            </Helmet>

            {sidebar && hasPermission && !showNotFound ? (
                <PageWrapperWithSidebar
                    className={cx({
                        'menu-closed': !isSideBarOpen,
                    })}
                >
                    <aside>
                        <div className="menu" id="page-sidebar-menu">
                            {sidebar}
                        </div>
                        <div className="menu-collapse">
                            <Button
                                variant="link"
                                onClick={toggleSidebar}
                                aria-expanded={isSideBarOpen}
                                aria-controls="page-sidebar-menu"
                                title={`${isSideBarOpen ? 'Collapse' : 'Expand'} Sidebar`}
                                aria-label={`${isSideBarOpen ? 'Collapse' : 'Expand'} Sidebar`}
                            >
                                {isSideBarOpen ? <FaAngleLeft /> : <FaAngleRight />}
                            </Button>
                        </div>
                    </aside>

                    <article>
                        {showPageTitle && <PageTitle>{title}</PageTitle>}

                        {children}
                    </article>
                </PageWrapperWithSidebar>
            ) : (
                <>
                    {hasPermission && showPageTitle && !showNotFound && <PageTitle>{title}</PageTitle>}

                    <PageWrapper>{hasPermission && !showNotFound ? children : <NotFound />}</PageWrapper>
                </>
            )}
        </Main>
    );
}

PageWithSidebar.propTypes = propTypes;
PageWithSidebar.defaultProps = defaultProps;

export default PageWithSidebar;
